import React, { useEffect, useState } from 'react';
import { Star, Flag, ChevronDown, ChevronUp, MapPin, ShieldOff } from 'lucide-react';
import { formatMWK } from './tokens';
import BlockRiderModal from './BlockRiderModal';

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'high', label: 'Highest earning' },
  { id: 'disputes', label: 'Disputes' },
];

/**
 * Screen 8 — Activity / Trip History.
 * Filter chips, expandable rows, low-rating dispute flag.
 */
const HistoryTab = ({ driverId }) => {
  const [filter, setFilter] = useState('all');
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [blockRider, setBlockRider] = useState(null);

  useEffect(() => {
    if (!driverId) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/rides/driver/${driverId}/history`)
      .then(r => r.json())
      .then(d => { if (!cancelled) { setRides(Array.isArray(d) ? d : (d?.items || [])); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [driverId]);

  const filtered = (() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const weekStart = todayStart - 6 * 86400000;
    let list = rides.filter(r => r.status === 'completed' || r.status === 'cancelled');
    if (filter === 'today') {
      list = list.filter(r => new Date(r.completed_at || r.created_at).getTime() >= todayStart);
    } else if (filter === 'week') {
      list = list.filter(r => new Date(r.completed_at || r.created_at).getTime() >= weekStart);
    } else if (filter === 'high') {
      list = [...list].sort((a, b) =>
        (Number(b.final_price || b.estimated_price || 0)) - (Number(a.final_price || a.estimated_price || 0))
      );
    } else if (filter === 'disputes') {
      list = list.filter(r => Number(r.driver_rating || 5) <= 3);
    }
    if (filter !== 'high') {
      list = [...list].sort((a, b) =>
        new Date(b.completed_at || b.created_at).getTime() - new Date(a.completed_at || a.created_at).getTime()
      );
    }
    return list;
  })();

  const handleDispute = (ride) => {
    // Phase-3 stub: persist a "dispute_filed" notification; admin tooling lands later.
    fetch(`${API}/api/notifications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: 'admin',
        title: `Driver disputed rating · ${ride.id?.slice(0, 8)}`,
        message: `Driver ${driverId} flagged a low rating on ride ${ride.id}`,
        type: 'driver_dispute',
        metadata: { ride_id: ride.id, driver_id: driverId, rating: ride.driver_rating },
      }),
    }).catch(() => { /* best-effort */ });
    alert('Dispute submitted — support will follow up.');
  };

  return (
    <div className="absolute inset-0 pt-3 pb-24 overflow-y-auto bg-gray-50" data-testid="driver-history-tab-v2">
      <div className="px-4 pt-20 pb-4">
        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 mb-3 no-scrollbar" data-testid="history-filter-chips">
          {FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`history-filter-${f.id}`}
              className={`shrink-0 px-3.5 h-8 rounded-full text-[12px] font-semibold transition-colors ${
                filter === f.id ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-emerald-400'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-sm text-gray-500 py-8">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8" data-testid="history-empty">No rides match this filter.</p>
        ) : (
          <ul className="space-y-2">
            {filtered.map(ride => (
              <RideRow
                key={ride.id}
                ride={ride}
                expanded={openId === ride.id}
                onToggle={() => setOpenId(openId === ride.id ? null : ride.id)}
                onDispute={() => handleDispute(ride)}
                onBlock={() => setBlockRider({
                  id: ride.rider_id,
                  name: ride.rider_first_name ? `${ride.rider_first_name} ${ride.rider_last_name || ''}`.trim() : null,
                })}
              />
            ))}
          </ul>
        )}
      </div>

      {blockRider && (
        <BlockRiderModal
          driverId={driverId}
          rider={blockRider}
          onClose={() => setBlockRider(null)}
          onBlocked={() => setBlockRider(null)}
        />
      )}
    </div>
  );
};

const RideRow = ({ ride, expanded, onToggle, onDispute, onBlock }) => {
  const date = ride.completed_at || ride.created_at;
  const earned = Number(ride.driver_earnings || ride.final_price || ride.estimated_price || 0);
  const rating = Number(ride.driver_rating || 0);
  const canDispute = rating > 0 && rating <= 3;
  const cancelled = ride.status === 'cancelled';
  const dt = (() => {
    try { return new Date(date); } catch { return null; }
  })();

  return (
    <li
      className={`bg-white rounded-2xl shadow-sm overflow-hidden border ${cancelled ? 'border-rose-100' : 'border-gray-100'}`}
      data-testid="history-row"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 text-left"
      >
        <div className="shrink-0 w-12 text-center">
          <p className="text-[10px] font-semibold uppercase text-gray-400">
            {dt ? dt.toLocaleDateString('en-US', { month: 'short' }) : '—'}
          </p>
          <p className="text-[18px] font-bold text-gray-900 leading-none tabular-nums">
            {dt ? dt.getDate() : '—'}
          </p>
          <p className="text-[10px] text-gray-400 tabular-nums">
            {dt ? dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] text-gray-700 truncate">
            <MapPin className="w-3 h-3 inline-block text-emerald-600 mr-1" />
            {ride.pickup_location?.address?.split(',')[0] || 'Pickup'}
          </p>
          <p className="text-[12px] text-gray-700 truncate mt-0.5">
            <span className="text-rose-500">→</span>{' '}
            {ride.dropoff_location?.address?.split(',')[0] || 'Destination'}
          </p>
          {rating > 0 && (
            <p className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-0.5">
              <Star className={`w-3 h-3 ${rating >= 4 ? 'text-amber-500 fill-amber-500' : 'text-gray-400'}`} />
              {rating.toFixed(1)}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p
            className={`text-[16px] font-bold tabular-nums ${cancelled ? 'text-gray-400 line-through' : 'text-gray-900'}`}
            data-testid="history-row-earnings"
          >
            {formatMWK(earned)}
          </p>
          {cancelled && <p className="text-[10px] text-rose-600 font-semibold">Cancelled</p>}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 ml-auto mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 ml-auto mt-1" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-3 py-3 bg-gray-50 space-y-1.5" data-testid="history-row-detail">
          <DetailLine label="Base fare" value={ride.base_fare || (earned - (ride.tip_amount || 0) - (ride.wait_fee || 0))} />
          {Number(ride.wait_fee || 0) > 0 && <DetailLine label="Wait fee" value={ride.wait_fee} />}
          {Number(ride.tip_amount || 0) > 0 && <DetailLine label="Tip" value={ride.tip_amount} />}
          {Number(ride.surge_multiplier || 0) > 1 && (
            <DetailLine label={`Surge ×${ride.surge_multiplier}`} value={(earned * (1 - 1 / ride.surge_multiplier))} />
          )}
          <div className="pt-1.5 border-t border-gray-200 flex justify-between items-center">
            <span className="text-[12px] font-semibold text-gray-900">Total earned</span>
            <span className="text-[14px] font-bold text-gray-900 tabular-nums">{formatMWK(earned)}</span>
          </div>
          {canDispute && (
            <button
              type="button"
              onClick={onDispute}
              data-testid="history-dispute-btn"
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-rose-50 text-rose-700 text-[12px] font-semibold hover:bg-rose-100"
            >
              <Flag className="w-3.5 h-3.5" /> Dispute this rating
            </button>
          )}
          {ride.rider_id && (
            <button
              type="button"
              onClick={onBlock}
              data-testid="history-block-btn"
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-gray-50 text-gray-700 text-[12px] font-semibold hover:bg-gray-100 border border-gray-200"
            >
              <ShieldOff className="w-3.5 h-3.5" /> Block this passenger
            </button>
          )}
        </div>
      )}
    </li>
  );
};

const DetailLine = ({ label, value }) => (
  <div className="flex justify-between text-[12px]">
    <span className="text-gray-600">{label}</span>
    <span className="tabular-nums text-gray-700">{formatMWK(value)}</span>
  </div>
);

export default HistoryTab;
